import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useApi } from "../../contexts/ApiContext";
import { colors, fontSizes } from "../../theme";
import FormLabel from "../FormLabel";
import { SelectFormInputProps } from "./SelectFormInput";
const StyledSelect = styled(Select)`
  margin: 0;
  padding: 0;

  "& ul.muimenu-list": {
    z-index: 1000;
  }
`;

export interface TagSelectInputProps
  extends Omit<Omit<SelectFormInputProps, "value">, "onChange"> {
  maxSelections?: number;
  onChange: (values: string[]) => void;
  value: string[];
}

const TagSelectInput: React.FC<TagSelectInputProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  options,
  caption,
  placeholder = "",
  maxSelections,
  ...rest
}) => {
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);

  const { isSubmitting, isLoading } = useApi();
  const isDisabled = isLoading || isSubmitting;

  const handleDelete = (tagToDelete: string) => {
    console.info("deleting", tagToDelete);
    onChange(value.filter((tag) => tag !== tagToDelete));
  };

  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;
    if (!value || !value.includes(selectedValue.toString())) {
      const newValues = [...(value ?? []), selectedValue.toString()];
      if (!maxSelections || newValues.length <= maxSelections) {
        onChange(newValues);
      }
    } else {
      onChange(value.filter((tag) => tag !== selectedValue));
    }
    setSelectOpen(true);
  };

  const valueRenderer = useMemo(
    () =>
      value && value.length ? (
        <Box
          display={"flex"}
          gap={1}
          justifyContent={"flex-start"}
          flexWrap="wrap"
          mb={1}
          width={500}
        >
          {value.map((selectedValue) => (
            <Chip
              sx={{ zIndex: 999 }}
              key={selectedValue}
              label={
                options.find((option) => option.value === selectedValue)?.label
              }
              onDelete={(event) => {
                console.info("deleting", selectedValue);
                event.preventDefault();
                event.stopPropagation();
                handleDelete(selectedValue);
              }}
            />
          ))}
        </Box>
      ) : (
        <Typography pt={0.5} fontSize={fontSizes.sm} color={colors.stone[500]}>
          {placeholder}
        </Typography>
      ),
    [value, placeholder]
  );

  return (
    <FormControl fullWidth error={error} disabled={isDisabled}>
      <FormLabel>{label}</FormLabel>
      {caption && (
        <Typography fontSize={fontSizes.sm} color={"greyColor"}>
          {caption}
        </Typography>
      )}
      <StyledSelect
        disabled={isDisabled}
        onClose={(event: any) => {
          if (event.currentTarget.tagName === "LI") {
            setSelectOpen(true);
          } else {
            setSelectOpen(false);
          }
        }}
        onOpen={(event: any) => {
          if (
            event.target.classList.contains("MuiChip-root") ||
            event.target.tagName === "svg" ||
            event.target.tagName === "path"
          ) {
            return;
          }
          setSelectOpen(true);
        }}
        displayEmpty
        value=""
        onChange={handleSelectChange}
        renderValue={() => valueRenderer}
        MenuProps={{ autoFocus: false, ref: selectRef }}
        open={selectOpen}
        {...rest}
      >
        {placeholder && (
          <MenuItem disabled value="">
            <Typography fontSize={fontSizes.sm} color={colors.stone[500]}>
              {placeholder}
            </Typography>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Checkbox checked={value?.includes(option.value)} />
              <Typography fontSize={fontSizes.sm} color={colors.stone[950]}>
                {option.label}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </StyledSelect>
      <FormHelperText>{helperText ? helperText : " "}</FormHelperText>
    </FormControl>
  );
};

export default TagSelectInput;
